import type { CreateQuizResponse, Credentials, TokenResponse } from '@elixir/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('Cannot reach the Elixir server. Check your internet connection.', 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.error ?? `Request failed (${res.status}).`, res.status);
  return data as T;
}

export const api = {
  createQuiz: (quizName: string, hostName: string) =>
    request<CreateQuizResponse>('/api/quizzes', { quizName, hostName }),
  getQuiz: (code: string) => request<{ code: string; name: string }>(`/api/quizzes/${encodeURIComponent(code)}`),
  join: (code: string, name: string) => request<Credentials>(`/api/quizzes/${encodeURIComponent(code)}/join`, { name }),
  hostRejoin: (code: string, hostKey: string) =>
    request<Credentials>(`/api/quizzes/${encodeURIComponent(code)}/host`, { hostKey }),
  tokens: (c: Credentials) =>
    request<TokenResponse>(`/api/quizzes/${encodeURIComponent(c.code)}/token`, {
      participantId: c.participantId,
      secret: c.secret,
    }),
};

// ---- Saved sessions, so a refresh or rejoin restores your seat and team ----

export interface SavedSession extends Credentials {
  name: string;
  hostKey?: string;
}

const key = (code: string) => `elixir.session.${code.toUpperCase()}`;

export const sessions = {
  load(code: string): SavedSession | null {
    try {
      return JSON.parse(localStorage.getItem(key(code)) ?? 'null');
    } catch {
      return null;
    }
  },
  save(s: SavedSession) {
    try {
      localStorage.setItem(key(s.code), JSON.stringify(s));
    } catch {
      /* private mode: the session lasts until the tab closes */
    }
  },
  clear(code: string) {
    try {
      localStorage.removeItem(key(code));
    } catch {
      /* ignore */
    }
  },
};
